/**
 * The musync bridge client: a second backend, deliberately not routed through
 * `api/controller.ts`.
 *
 * Every other client in this app talks to the *music server* (Jellyfin,
 * Navidrome, or Subsonic) the user is logged into; that is what
 * `ApiController`/`ServerType` exist to abstract over. musync's API is a
 * different thing entirely: a search-and-acquire bridge that lives on its own
 * host, has its own sessions, and has no `ServerType`. Bolting it
 * onto the server-type switch would mean inventing a fourth fake "server"
 * with none of the properties the other three share (library browsing,
 * streaming, playlists). A small, separate axios client is the honest shape.
 *
 * Configuration follows the exact pattern `SERVER_URL`/`SERVER_LOCK` already
 * use in this app: `MUSYNC_URL` is injected onto `window` by `settings.js` at
 * container start (see `settings.js.template`, `ng.conf.template`), so the
 * fork's own Dockerfile can point a deployed instance at any musync install
 * without a rebuild.
 *
 * Auth is per user: there is no deployment-wide token. Every request carries
 * the signed-in Feishin user's own musync session (see `musync-session.ts`),
 * obtained by exchanging their Navidrome credential. A 401 drops that session,
 * re-exchanges once and retries the request once; a second 401 surfaces to the
 * caller. Only Navidrome logins can be exchanged, so musync features are hidden
 * for Jellyfin and plain Subsonic servers.
 */
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
    useQuery,
} from '@tanstack/react-query';
import axios, { AxiosInstance, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { useCallback } from 'react';

import {
    getMusyncSession,
    MusyncSessionError,
    musyncSessionQueryKey,
    musyncSessions,
    readMusyncBaseUrl,
} from '/@/renderer/api/musync/musync-session';
import {
    assertMusyncRequestCurrent,
    authorizeMusyncRequest,
    isMusyncLoginChangedError,
    isNavidromeServer,
    MUSYNC_MUTATION_OPTIONS,
    MUSYNC_SIGN_IN_FAILED_MESSAGE,
    MUSYNC_UNREACHABLE_MESSAGE,
    MusyncBoundRequest,
    MusyncPermissions,
    musyncPermissions,
    musyncSessionCacheKey,
    musyncSessionErrorText,
    retryUnauthorizedMusyncRequest,
} from '/@/renderer/api/musync/musync-session-core';
import { useAuthStore } from '/@/renderer/store/auth.store';

export { MUSYNC_MUTATION_OPTIONS, MUSYNC_SIGN_IN_FAILED_MESSAGE };

type MutationIntent<V> = { pin: ReturnType<typeof musyncSessions.pin>; variables: V };

/** Capture intent at the public call boundary, not in TanStack's async mutationFn. */
export const useMusyncMutation = <D, E = Error, V = void, C = unknown>(
    options: Omit<UseMutationOptions<D, E, V, C>, 'mutationFn'> & {
        mutationFn: (variables: V, client: AxiosInstance) => Promise<D>;
    },
): UseMutationResult<D, E, V, C> => {
    const mutation = useMutation<D, E, MutationIntent<V>, C>({
        ...options,
        ...MUSYNC_MUTATION_OPTIONS,
        mutationFn: ({ pin, variables }) => options.mutationFn(variables, musyncClient(pin)),
        onError: (error, { variables }, result, context) =>
            options.onError?.(error, variables, result, context),
        onMutate: options.onMutate
            ? ({ variables }, context) => options.onMutate!(variables, context)
            : undefined,
        onSettled: (data, error, { variables }, result, context) =>
            options.onSettled?.(data, error, variables, result, context),
        onSuccess: (data, { variables }, result, context) =>
            options.onSuccess?.(data, variables, result, context),
    });
    const { mutateAsync: execute } = mutation;
    const mutateAsync = useCallback<UseMutationResult<D, E, V, C>['mutateAsync']>(
        (variables, callbacks) => {
            const intent = { pin: musyncSessions.pin(), variables };
            return execute(intent, {
                onError: (error, { variables }, result, context) =>
                    callbacks?.onError?.(error, variables, result, context),
                onSettled: (data, error, { variables }, result, context) =>
                    callbacks?.onSettled?.(data, error, variables, result, context),
                onSuccess: (data, { variables }, result, context) =>
                    callbacks?.onSuccess?.(data, variables, result, context),
            });
        },
        [execute],
    );
    const mutate = useCallback<UseMutationResult<D, E, V, C>['mutate']>(
        (variables, callbacks) => {
            void mutateAsync(variables, callbacks).catch(() => undefined);
        },
        [mutateAsync],
    );
    // Only the private observer sees the envelope; callers keep their variables,
    // callback context, result discriminants, reset and fire-and-forget API.
    return {
        ...mutation,
        mutate,
        mutateAsync,
        variables: mutation.variables?.variables,
    } as UseMutationResult<D, E, V, C>;
};

let client: AxiosInstance | null = null;
let clientBaseUrl: null | string = null;

/** A request pinned to the sign-in it was started under (see `authorizeMusyncRequest`). */
type BoundConfig = InternalAxiosRequestConfig & MusyncBoundRequest;

const readBaseUrl = readMusyncBaseUrl;

/**
 * True when this deployment has a musync backend and the current Feishin login
 * is a Navidrome server, the only kind whose credential musync can verify.
 */
export const isMusyncConfigured = (): boolean =>
    readBaseUrl().length > 0 && isNavidromeServer(useAuthStore.getState().currentServer);

/** Reactive `isMusyncConfigured`: re-renders when the Feishin login changes. */
export const useIsMusyncConfigured = (): boolean =>
    useAuthStore((state) => readBaseUrl().length > 0 && isNavidromeServer(state.currentServer));

/**
 * What the signed-in musync role may do (`me.capabilities` of the session
 * exchange). Reading it signs in if needed; while unknown, everything is
 * allowed and musync itself decides.
 */
export const useMusyncPermissions = (): MusyncPermissions => {
    const cacheKey = useAuthStore((state) => {
        const server = state.currentServer;
        if (!server || !isNavidromeServer(server) || !server.id || !server.username) return null;
        return musyncSessionCacheKey(server.id, server.username);
    });
    const enabled = readBaseUrl().length > 0 && Boolean(cacheKey);
    const query = useQuery({
        enabled,
        queryFn: async () => (await getMusyncSession()).capabilities ?? null,
        queryKey: musyncSessionQueryKey(cacheKey),
        retry: false,
        staleTime: Infinity,
        throwOnError: false,
    });
    return musyncPermissions(enabled ? query.data : null);
};

/** Translate musync transport failures into guidance the user can act on. */
export const getMusyncErrorMessage = (error: unknown): string => {
    if (isMusyncLoginChangedError(error)) return (error as Error).message;
    if (error instanceof MusyncSessionError) {
        return musyncSessionErrorText({ detail: error.detail, status: error.status });
    }

    if (!isAxiosError(error)) {
        return 'musync request failed. Try again.';
    }

    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    // Reaching here with a 401 means the session was already re-exchanged once.
    if (status === 401) {
        return MUSYNC_SIGN_IN_FAILED_MESSAGE;
    }

    if (typeof detail === 'string' && detail) {
        return detail;
    }

    if (status === 403) {
        return 'Your musync account is not allowed to do this.';
    }

    if (status === 408 || status === 504 || error.code === 'ECONNABORTED') {
        return 'musync took too long to respond. Try again.';
    }

    if (status && status >= 500) {
        return 'musync could not complete this search. Try again shortly.';
    }

    if (!error.response) {
        return MUSYNC_UNREACHABLE_MESSAGE;
    }

    return 'musync rejected the request. Check the backend configuration and try again.';
};

/**
 * The axios instance, rebuilt if `window.MUSYNC_URL` changes underneath us
 * (settings.js is static per container, but hot-reload in dev can still swap
 * it). The bearer token is attached per request, not baked into the
 * instance, because it belongs to whoever is signed in right now.
 */
export const musyncClient = (pin?: ReturnType<typeof musyncSessions.pin>): AxiosInstance => {
    const baseUrl = readBaseUrl();

    if (pin === undefined && client && clientBaseUrl === baseUrl) {
        return client;
    }

    const instance = axios.create({
        ...(pin === undefined ? {} : musyncClient().defaults),
        baseURL: `${baseUrl}/api/v1`,
        timeout: 15_000,
    });

    // Each request is pinned to the sign-in it started under: a login change
    // mid-flight (other user, or the same user signing in again) cancels it
    // instead of sending or retrying it under the new login, and its late
    // answer never reaches the new login's caches or screens.
    instance.interceptors.request.use((config) => {
        if (pin !== undefined) (config as BoundConfig)._musyncPin = pin;
        return authorizeMusyncRequest(musyncSessions, config as BoundConfig);
    });

    instance.interceptors.response.use(
        (response) => {
            assertMusyncRequestCurrent(musyncSessions, response.config as BoundConfig);
            return response;
        },
        async (error: unknown) => {
            if (!isAxiosError(error) || !error.config) throw error;
            const config = error.config as BoundConfig;
            assertMusyncRequestCurrent(musyncSessions, config);
            if (error.response?.status !== 401) throw error;
            // The request interceptor picks the fresh token up from the cache.
            return retryUnauthorizedMusyncRequest(musyncSessions, config, error, (again) =>
                instance.request(again),
            );
        },
    );

    if (pin === undefined) {
        client = instance;
        clientBaseUrl = baseUrl;
    }
    return instance;
};
